import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable,} from 'rxjs';
import { map } from 'rxjs/operators';
import { environment as ENV } from '@environment/environment';
import { IRequestCreateCardViewModel, SuccessSavedCard, NewUserCard } from '@interfaces/i-request-create-card-model';
import { IRequestDefaultCardViewModel } from '@interfaces/i-request-default-card-model';
import { IRequestRemoveCardViewModel } from '@interfaces/i-request-remove-card-model';
import { IResponseCardViewModel, IResponseMinimalCard, IResponseMinimalCardWrapper  } from '@interfaces/i-response-card-view-model';
import {  EditCard  } from '@interfaces/i-request-edit-card-model';
import { PaymentMethodProvider } from '@interfaces/i-payment-methods.interface';
import { IPreviewTaxEstimationResponse, ITaxTransactionRequest } from '@app/api-client/models/v2/i-tax-transaction-request';
import { IPaymentCardRequest } from '@app/api-client/models/v2/i-payment-card-request';

@Injectable({
  providedIn: 'root',
})
export class WalletV3Service {

  private API = `${ENV.webApi}v2/wallet`;
  private APISOA = `${ENV.soaApiBaseUrl}`;


  constructor(private http: HttpClient) {}

  public getUserCreditCards(userId?: string): Observable<IResponseCardViewModel[]> {
    const params = userId ? { userId } : {};
    return this.http.get<IResponseCardViewModel[]>(`${this.API}/GetUserCCs`, { params });
  }

  public getUserCreditCardsMinimal(userId?: string): Observable<IResponseMinimalCard[]> {
    const params = userId ? { userId } : {};
    return this.http.get<IResponseMinimalCardWrapper>(`${this.APISOA}/Wallet/user/cards`, {params}).pipe(
      map(res => res.value.map(card => ({
        cardId: card.id,
        expMonth: card.cardExpMonth,
        expYear: card.cardExpYear,
        isDefault: card.isDefault,
        cardImageUrl: card.cardImageUrl,
        last4: card.cardLast4,
        cardBrandName: card.cardBrandName,
        expired: undefined,
        openOptions: false,
        provider: 'chargebee',
        countryCode: card.countryCode
      })))
    );
  }

  public editUserCreditCard(payload: EditCard): Observable<boolean> {
    return this.http.put<boolean>(
      `${this.APISOA}Wallet/card/update`,payload);
  }

  public addUserCreditCard(payload: NewUserCard): Observable<any> {
    const url = `${this.APISOA}Wallet/card/save`;

    return this.http.post<boolean>(url, payload)
  }

  public removeUserCard(deletedCardId: string, payload: any = {}): Observable<boolean> {
    const url = `${this.APISOA}Wallet/card/delete/${deletedCardId}`;

    return this.http.delete<boolean>(url, {body: payload})
  }

  public changePrimaryCard(newPrimaryCardId: string): Observable<boolean> {
    const url = `${this.APISOA}Wallet/card/change-primary/${newPrimaryCardId}`;
    return this.http.put<boolean>(url, null);
  }

  public updateDefaultCard(payload: IRequestDefaultCardViewModel): Observable<boolean> {
    return this.http.post<boolean>(`${this.API}/UpdateDefaultCard`, payload);
  }

  public removeCard(payload: IRequestRemoveCardViewModel): Observable<boolean> {
    return this.http.post<boolean>(`${this.API}/RemoveCC`, payload);
  }

  public getPaymentMethods(userId: string, provider: string): Observable<PaymentMethodProvider> {
    const params = { userId, provider };
    return this.http.get<PaymentMethodProvider>(`${this.API}/GetPaymentMethods`, { params });
  }

  public addCard(payload: IRequestCreateCardViewModel): Observable<SuccessSavedCard> {
    return this.http.post<SuccessSavedCard>(`${this.API}/SaveCC`, payload);
  }

  public previewTaxEstimation(payload: ITaxTransactionRequest): Observable<IPreviewTaxEstimationResponse> {
    return this.http.post<any>(`${this.APISOA}Taxes/preview`, payload);
  }

  public payWithCreditCard(payload: IPaymentCardRequest, userId?: string): Observable<any> {
    const params = userId ? { userId } : {};
    return this.http.post<any>(`${this.APISOA}/Payment/credit-card`, payload, {params});
  }
}
